import { useDebugStore } from '@client/stores/DebugStore';
import { getFlagSync } from '@client/utils/feature-flags';
import { RouteChunkStallError } from '@client/utils/routing/route-chunk-stall';
import { isRouteChunkWarm } from '@client/utils/routing/route-chunk-warmth';
import type { AnyRoute, RegisteredRouter, Router } from '@tanstack/react-router';
import type { RouteIds, RoutePaths } from '@tanstack/router-core';

/**
 * Type-safe route ID (path pattern) for registered routes.
 * Use this for preloading routes by their path pattern.
 * @example '/console/$orgId/claims/details/$id/manage'
 */
export type RouteId = RoutePaths<RegisteredRouter['routeTree']>;

/**
 * Global set tracking all preloaded route IDs.
 * Prevents duplicate preloading across different preloader mechanisms
 * (idle preloader, PageController, etc.)
 */
const preloadedRoutes = new Set<string>();

/**
 * Why a route's chunk is being preloaded. `intent` is a hover or focus on a link the user is
 * about to click; `early` is the preloader's pass over the page's likeliest next clicks as soon as
 * it settles; `idle` is its background walk once the user goes idle; `redirect` is a `beforeLoad`
 * warming the page it is about to redirect to.
 */
export type RoutePreloadSource = 'intent' | 'early' | 'idle' | 'redirect';

/** The sources that are the app's own background preloading rather than the user's intent. */
export type BackgroundPreloadSource = Exclude<RoutePreloadSource, 'intent'>;

/**
 * The source of the preload that found each route's chunk cold and loaded it, until a
 * navigation to the page takes the credit. Claimed synchronously before the load is awaited, so
 * of two concurrent preloads of one route only the first claims it, and a preload of a chunk
 * that was already in memory claims nothing. Each claim is its own object, so a load that
 * settles after its claim was taken releases nothing it does not own.
 */
const warmedBy = new Map<string, { source: RoutePreloadSource }>();

/**
 * Checks if a route has already been preloaded.
 * Useful for debugging and testing.
 */
export function isRoutePreloaded(routeId: string): boolean {
    return preloadedRoutes.has(routeId);
}

/**
 * Which of the app's background preloads (the early pass, the idle walk or a redirect warm-up)
 * loaded this route's chunk while it was still cold, if one did, as opposed to a hover intent
 * preload, the router on an earlier visit, or a preload that found it already in memory. Takes
 * the credit, whatever its source: the navigation that reaches the page uses it up, so a later
 * visit, which finds the chunk loaded by this one, is not credited too.
 */
export function takeBackgroundPreloadSource(routeId: string): BackgroundPreloadSource | undefined {
    const source = warmedBy.get(routeId)?.source;
    warmedBy.delete(routeId);
    return source === 'intent' ? undefined : source;
}

/** Loads one route's chunk, recording which source warmed it and that it is now preloaded. */
async function loadRouteChunkFrom(route: AnyRoute, router: Router<any, any>, source: RoutePreloadSource) {
    const claim = !warmedBy.has(route.id) && !isRouteChunkWarm(route) ? { source } : undefined;
    if (claim) {
        warmedBy.set(route.id, claim);
    }
    const release = () => {
        if (claim && warmedBy.get(route.id) === claim) {
            warmedBy.delete(route.id);
        }
    };
    try {
        await router.loadRouteChunk(route);
    } catch (error) {
        release();
        // A navigation to this route is waiting on the same unanswered import and reports it
        // (`guardRouteChunkStalls`). For a preload the stall only means nothing was warmed.
        if (error instanceof RouteChunkStallError) {
            return;
        }
        throw error;
    }
    // `lazyRouteComponent` catches a failed import: `preload()` fulfils with the component still
    // cold and keeps the error for the render to throw. Such a load warmed nothing, so it neither
    // keeps its claim nor marks the route preloaded, and whichever load retries it is credited.
    if (!isRouteChunkWarm(route)) {
        release();
        return;
    }
    preloadedRoutes.add(route.id);
}

/**
 * Determines whether preload logs should be displayed.
 * Logs show when BOTH:
 * - isPreloadLogsEnabled is true (checkbox in debug panel)
 * - Debug mode is active (enable-debug-info feature flag OR isForceDebugEnabledFlag)
 */
function shouldLogPreload(): boolean {
    const { isForceDebugEnabledFlag, isPreloadLogsEnabled } = useDebugStore.getState();
    const isDebugInfoEnabled = getFlagSync('enable-debug-info', false);
    // Match useIsDebugInfoEnabled logic: feature flag OR force debug
    return isPreloadLogsEnabled && (isDebugInfoEnabled || isForceDebugEnabledFlag);
}

/**
 * Pre-loads the JS chunk for a specific route
 * @param route - The route to preload
 * @param router - The TanStack router instance
 * @param source - Why it is preloaded; hover and focus handlers keep the default `intent`
 * @returns Promise that resolves when the chunk is loaded
 */
export async function preloadRouteChunk(
    route: AnyRoute,
    router: Router<any, any>,
    source: RoutePreloadSource = 'intent',
): Promise<void> {
    if (!route.options?.component) {
        return;
    }

    // Skip if already preloaded
    if (preloadedRoutes.has(route.id)) {
        return;
    }

    if (shouldLogPreload()) {
        console.log('[Route Preloader] Pre-loading page', route.id);
    }
    // Let errors propagate to the global chunk error handler
    await loadRouteChunkFrom(route, router, source);
}

/**
 * Pre-loads the child JS chunks for a given route
 * @param route - The parent route whose children should be preloaded
 * @param router - The TanStack router instance
 * @returns Promise that resolves when all child chunks are loaded
 */
export async function preloadChildRouteChunks(route: AnyRoute, router: Router<any, any>): Promise<void> {
    const children = route.children as Record<string, AnyRoute> | undefined;

    if (!children || typeof children !== 'object') {
        return;
    }

    const childRoutes = Object.values(children);

    if (childRoutes.length === 0) {
        return;
    }

    const enableLogging = shouldLogPreload();

    // Let errors propagate to the global chunk error handler
    await Promise.all(
        childRoutes
            .filter((child) => child.options?.component && !preloadedRoutes.has(child.id))
            .map(async (child) => {
                if (enableLogging) {
                    console.log('[Route Preloader] Pre-loading child page', child.id);
                }
                await loadRouteChunkFrom(child, router, 'intent');
            }),
    );
}

/**
 * Pre-loads a route's JS chunk by route ID (path pattern).
 * No params needed - just the route path like '/console/$orgId/claims/details/$id/manage'
 *
 * @param routeId - The route path pattern (e.g., '/console/$orgId/claims/details/$id/manage')
 * @param router - The TanStack router instance
 * @returns Promise that resolves when the chunk is loaded
 *
 * @example
 * // Preload a route chunk by its path pattern
 * await preloadRouteChunkById('/console/$orgId/claims/details/$id/manage', router);
 */
export async function preloadRouteChunkById(routeId: RouteId, router: Router<any, any>): Promise<void> {
    const route = router.routesById[routeId];
    if (route) {
        await preloadRouteChunk(route, router);
    } else if (shouldLogPreload()) {
        console.warn('[Route Preloader] Route not found:', routeId);
    }
}

/**
 * The app router, for callers that are not handed it. A route's `beforeLoad` gets the router
 * context but not the router itself.
 */
let attachedRouter: Router<any, any> | undefined;

/** Names the router `startRouteChunkPreloads` loads through. Called once, where the router is created. */
export function attachRoutePreloader(router: Router<any, any>): void {
    attachedRouter = router;
}

/**
 * Starts loading the code-split components of `routeIds` without waiting for them. For a
 * `beforeLoad` that awaits a request and then redirects: the router loads a route's chunks only
 * once a navigation matches it, so the destination's chunks would otherwise wait for the
 * request and the redirect. A failed load is left to the router, which loads the matched routes
 * again after the redirect and handles a chunk error there.
 *
 * Takes route IDs, the keys of `routesById`, rather than `RouteId` full paths: under a route
 * group or pathless layout the two differ (`/(authentication)/callback` against `/callback`),
 * and a full path would find no route and load nothing.
 */
export function startRouteChunkPreloads(routeIds: readonly RouteIds<RegisteredRouter['routeTree']>[]): void {
    const router = attachedRouter;
    if (!router) {
        return;
    }
    for (const routeId of routeIds) {
        const route = router.routesById[routeId];
        if (route) {
            preloadRouteChunk(route, router, 'redirect').catch(() => undefined);
        }
    }
}
